import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NeuralBackground from '../NeuralBackground/NeuralBackground';
import { useLanguage } from '../../i18n/LanguageContext';
import './Hero.css';

/* ── Constants ── */
const ROTATING_COLORS = ['#00F2FF', '#C9A84C', '#00F2FF', '#C9A84C', '#00F2FF', '#C9A84C'];

/* ── 8-Pointed Islamic Star (Octagram) ── */
const IslamicStar = ({ size = 24, color = '#C9A84C', opacity = 0.15, className = '' }) => {
  const r = size / 2;
  const outerR = r;
  const innerR = r * 0.38;
  const pts = [];
  for (let i = 0; i < 8; i++) {
    const oAngle = (Math.PI * 2 * i) / 8 - Math.PI / 2;
    const iAngle = oAngle + Math.PI / 8;
    pts.push(`${r + outerR * Math.cos(oAngle)},${r + outerR * Math.sin(oAngle)}`);
    pts.push(`${r + innerR * Math.cos(iAngle)},${r + innerR * Math.sin(iAngle)}`);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className} style={{ opacity }}>
      <polygon points={pts.join(' ')} fill={color} />
    </svg>
  );
};

/* ── Floating star positions ── */
const FLOATING_STARS = [
  { size: 40, color: '#C9A84C', opacity: 0.06, top: '12%', left: '6%', delay: 0 },
  { size: 24, color: '#00F2FF', opacity: 0.05, top: '22%', right: '10%', delay: 1.5 },
  { size: 50, color: '#C9A84C', opacity: 0.04, bottom: '25%', left: '12%', delay: 3 },
  { size: 18, color: '#00F2FF', opacity: 0.08, top: '55%', left: '42%', delay: 0.8 },
  { size: 32, color: '#C9A84C', opacity: 0.05, bottom: '35%', right: '5%', delay: 2.2 },
  { size: 22, color: '#00F2FF', opacity: 0.06, top: '8%', right: '30%', delay: 4 },
];

const Hero = () => {
  const { t } = useLanguage();
  const rotatingWords = t('hero.rotatingWords');
  const focusAreas = t('hero.focusAreas');
  const [wordIndex, setWordIndex] = useState(0);

  const rotateWord = useCallback(() => {
    setWordIndex((prev) => (prev + 1) % rotatingWords.length);
  }, [rotatingWords]);

  useEffect(() => {
    const interval = setInterval(rotateWord, 2800);
    return () => clearInterval(interval);
  }, [rotateWord]);

  useEffect(() => {
    setWordIndex(0);
  }, [rotatingWords]);

  const currentWord = {
    text: rotatingWords[wordIndex],
    color: ROTATING_COLORS[wordIndex % ROTATING_COLORS.length],
  };

  return (
    <section className="hero" id="home">
      {/* ── Background layers ── */}
      <div className="hero__bg">
        <div className="hero__bg-gradient" />
        <NeuralBackground />
      </div>

      {/* ── Zellige tile pattern overlay ── */}
      <svg className="hero__zellige" aria-hidden="true">
        <defs>
          <pattern id="zellige" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            {/* Two overlapping rotated squares → 8-pointed star motif */}
            <rect x="20" y="20" width="20" height="20" fill="none" stroke="#C9A84C" strokeWidth="0.4" opacity="0.07" />
            <rect x="20" y="20" width="20" height="20" fill="none" stroke="#C9A84C" strokeWidth="0.4" opacity="0.07" transform="rotate(45 30 30)" />
            {/* Connecting lines to adjacent stars */}
            <line x1="0" y1="30" x2="20" y2="30" stroke="#C9A84C" strokeWidth="0.3" opacity="0.04" />
            <line x1="40" y1="30" x2="60" y2="30" stroke="#C9A84C" strokeWidth="0.3" opacity="0.04" />
            <line x1="30" y1="0" x2="30" y2="20" stroke="#C9A84C" strokeWidth="0.3" opacity="0.04" />
            <line x1="30" y1="40" x2="30" y2="60" stroke="#C9A84C" strokeWidth="0.3" opacity="0.04" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#zellige)" />
      </svg>

      {/* ── Floating Islamic stars ── */}
      {FLOATING_STARS.map((s, i) => (
        <div
          key={i}
          className="hero__float-star"
          style={{
            top: s.top,
            bottom: s.bottom,
            left: s.left,
            right: s.right,
            animationDelay: `${s.delay}s`,
          }}
        >
          <IslamicStar size={s.size} color={s.color} opacity={s.opacity} />
        </div>
      ))}

      {/* ── Ambient orbs (warmer gold emphasis) ── */}
      <div className="hero__orb hero__orb--gold" />
      <div className="hero__orb hero__orb--cyan" />

      {/* ── Content ── */}
      <div className="hero__content">
        {/* Left: Text */}
        <motion.div
          className="hero__text"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.2 }}
        >
          {/* Eyebrow with Moroccan stars */}
          <div className="hero__eyebrow">
            <IslamicStar size={10} color="#C9A84C" opacity={0.5} />
            <span className="hero__eyebrow-line" />
            <span className="hero__eyebrow-text">{t('hero.eyebrow')}</span>
            <span className="hero__eyebrow-line" />
            <IslamicStar size={10} color="#C9A84C" opacity={0.5} />
          </div>

          {/* Headline with rotating word */}
          <h1 className="hero__title">
            {t('hero.titleLine')}
            <br />
            <span className="hero__title-rotating">
              <AnimatePresence mode="wait">
                <motion.span
                  key={wordIndex}
                  className="hero__title-word"
                  style={{ color: currentWord.color }}
                  initial={{ y: 40, opacity: 0, filter: 'blur(8px)' }}
                  animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
                  exit={{ y: -40, opacity: 0, filter: 'blur(8px)' }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  {currentWord.text}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>

          {/* Description */}
          <p className="hero__description">
            {t('hero.description')}
          </p>

          {/* Dual CTAs */}
          <div className="hero__cta-group">
            <a href="#contact" className="hero__cta hero__cta--primary">
              <span>{t('hero.ctaPrimary')}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="#demo" className="hero__cta hero__cta--ghost">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M4 2l8 5-8 5V2z" fill="currentColor" />
              </svg>
              <span>{t('hero.ctaSecondary')}</span>
            </a>
          </div>

          {/* Trust line */}
          <div className="hero__trust">
            <span className="hero__trust-label">{t('hero.focusLabel')}</span>
            <div className="hero__trust-logos">
              {focusAreas.map((name) => (
                <span key={name} className="hero__trust-logo">{name}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Right: Moroccan Arch + Logo */}
        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.4 }}
        >
          <div className="hero__arch">
            <svg className="hero__arch-svg" viewBox="0 0 340 440" fill="none" aria-hidden="true">
              <defs>
                {/* Gold gradient for outer arch */}
                <linearGradient id="archGradient" x1="170" y1="20" x2="170" y2="440" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.7" />
                  <stop offset="40%" stopColor="#C9A84C" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#C9A84C" stopOpacity="0.08" />
                </linearGradient>

                {/* Inner arch gradient */}
                <linearGradient id="archGradientInner" x1="170" y1="55" x2="170" y2="440" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.35" />
                  <stop offset="50%" stopColor="#C9A84C" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#C9A84C" stopOpacity="0.03" />
                </linearGradient>

                {/* Glow filter for main arch */}
                <filter id="archGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                {/* Portal glow inside arch */}
                <radialGradient id="portalGlow" cx="50%" cy="38%" r="45%">
                  <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.06" />
                  <stop offset="40%" stopColor="#00F2FF" stopOpacity="0.03" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </radialGradient>

                {/* Decorative pattern for the arch band */}
                <pattern id="archDeco" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                  <circle cx="7" cy="7" r="1.2" fill="#C9A84C" opacity="0.2" />
                  <rect x="3.5" y="3.5" width="7" height="7" rx="0.5" fill="none" stroke="#C9A84C" strokeWidth="0.3" opacity="0.12" transform="rotate(45 7 7)" />
                </pattern>
              </defs>

              {/* Portal inner glow */}
              <ellipse cx="170" cy="190" rx="110" ry="120" fill="url(#portalGlow)" />

              {/* Outer horseshoe arch — the signature Moroccan shape */}
              <path
                d="M 80,440 L 80,215 C 55,135 95,28 170,28 C 245,28 285,135 260,215 L 260,440"
                stroke="url(#archGradient)"
                strokeWidth="2"
                fill="none"
                filter="url(#archGlow)"
              />

              {/* Inner horseshoe arch */}
              <path
                d="M 108,440 L 108,225 C 88,155 118,62 170,62 C 222,62 252,155 232,225 L 232,440"
                stroke="url(#archGradientInner)"
                strokeWidth="1"
                fill="none"
              />

              {/* Decorative band between outer and inner arches */}
              <clipPath id="archBand">
                <path d="
                  M 80,440 L 80,215 C 55,135 95,28 170,28 C 245,28 285,135 260,215 L 260,440
                  L 232,440 L 232,225 C 252,155 222,62 170,62 C 118,62 88,155 108,225 L 108,440 Z
                " />
              </clipPath>
              <rect width="340" height="440" fill="url(#archDeco)" clipPath="url(#archBand)" />

              {/* Keystone 8-pointed star at the apex */}
              <g transform="translate(156, 10)">
                <polygon
                  points="14,0 17,5.5 23,5.5 18.5,9.5 20,16 14,12 8,16 9.5,9.5 5,5.5 11,5.5"
                  fill="#C9A84C"
                  opacity="0.5"
                  filter="url(#archGlow)"
                />
              </g>

              {/* Spring-point stars (where arch meets pillar) */}
              <g transform="translate(68, 206)">
                <polygon
                  points="7,0 8.5,3 12,3 9.5,5 10.5,8.5 7,6.5 3.5,8.5 4.5,5 2,3 5.5,3"
                  fill="#C9A84C"
                  opacity="0.3"
                />
              </g>
              <g transform="translate(250, 206)">
                <polygon
                  points="7,0 8.5,3 12,3 9.5,5 10.5,8.5 7,6.5 3.5,8.5 4.5,5 2,3 5.5,3"
                  fill="#C9A84C"
                  opacity="0.3"
                />
              </g>

              {/* Decorative dots along outer arch curve */}
              <circle cx="80" cy="340" r="1.5" fill="#C9A84C" opacity="0.25" />
              <circle cx="79" cy="300" r="1.5" fill="#C9A84C" opacity="0.25" />
              <circle cx="77" cy="260" r="1.5" fill="#C9A84C" opacity="0.2" />
              <circle cx="70" cy="190" r="1.5" fill="#C9A84C" opacity="0.2" />
              <circle cx="78" cy="150" r="1.5" fill="#C9A84C" opacity="0.15" />
              <circle cx="100" cy="90" r="1.5" fill="#C9A84C" opacity="0.15" />
              <circle cx="135" cy="48" r="1.5" fill="#C9A84C" opacity="0.12" />

              <circle cx="260" cy="340" r="1.5" fill="#C9A84C" opacity="0.25" />
              <circle cx="261" cy="300" r="1.5" fill="#C9A84C" opacity="0.25" />
              <circle cx="263" cy="260" r="1.5" fill="#C9A84C" opacity="0.2" />
              <circle cx="270" cy="190" r="1.5" fill="#C9A84C" opacity="0.2" />
              <circle cx="262" cy="150" r="1.5" fill="#C9A84C" opacity="0.15" />
              <circle cx="240" cy="90" r="1.5" fill="#C9A84C" opacity="0.15" />
              <circle cx="205" cy="48" r="1.5" fill="#C9A84C" opacity="0.12" />

              {/* Subtle horizontal lines inside arch */}
              <line x1="115" y1="320" x2="225" y2="320" stroke="rgba(201,168,76,0.04)" strokeWidth="0.5" />
              <line x1="112" y1="280" x2="228" y2="280" stroke="rgba(201,168,76,0.03)" strokeWidth="0.5" />
              <line x1="120" y1="360" x2="220" y2="360" stroke="rgba(201,168,76,0.04)" strokeWidth="0.5" />
            </svg>

            {/* Logo centered inside the arch */}
            <div className="hero__logo-wrap">
              <img
                src="/Logo.png"
                alt={t('brand.logoAlt')}
                className="hero__logo-img"
              />
            </div>
          </div>

          {/* Floating stat badges with star accent */}
          <motion.div
            className="hero__badge hero__badge--left"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <IslamicStar size={10} color="#00F2FF" opacity={0.4} className="hero__badge-star" />
            <span className="hero__badge-value">{t('hero.badgeLeftValue')}</span>
            <span className="hero__badge-label">{t('hero.badgeLeftLabel')}</span>
          </motion.div>
          <motion.div
            className="hero__badge hero__badge--right"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5, duration: 0.6 }}
          >
            <IslamicStar size={10} color="#C9A84C" opacity={0.4} className="hero__badge-star" />
            <span className="hero__badge-value">{t('hero.badgeRightValue')}</span>
            <span className="hero__badge-label">{t('hero.badgeRightLabel')}</span>
          </motion.div>
        </motion.div>
      </div>

      {/* ── Moroccan geometric border at bottom ── */}
      <div className="hero__moroccan-border">
        <svg viewBox="0 0 1440 24" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <pattern id="borderStars" x="0" y="0" width="32" height="24" patternUnits="userSpaceOnUse">
              <polygon
                points="16,2 18.5,7.5 24.5,7.5 19.5,11.5 21.5,17.5 16,14 10.5,17.5 12.5,11.5 7.5,7.5 13.5,7.5"
                fill="none"
                stroke="#C9A84C"
                strokeWidth="0.4"
                opacity="0.25"
              />
            </pattern>
          </defs>
          <rect width="1440" height="24" fill="url(#borderStars)" />
          <line x1="0" y1="12" x2="1440" y2="12" stroke="rgba(201,168,76,0.08)" strokeWidth="0.5" />
        </svg>
      </div>

      {/* Bottom fade */}
      <div className="hero__bottom-fade" />

      {/* Scroll indicator */}
      <motion.div
        className="hero__scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
      >
        <div className="hero__scroll-line" />
        <span className="hero__scroll-text">{t('hero.scrollText')}</span>
      </motion.div>
    </section>
  );
};

export default Hero;
